/**
 * Iter-237 — Creator Attribution Panel
 *
 * Drop-in component used by:
 *   - /app/frontend/src/pages/insights/EventInsightsDashboard.js
 *   - /app/frontend/src/pages/insights/ClientInsightsRollup.js
 *
 * Renders the Awareness→Revenue funnel, top creators, top promo codes,
 * revenue by market, revenue by campaign type, revenue by objective,
 * ROAS, and conversion-rate KPIs. Adapts UI based on campaign objective.
 */
import React from 'react';
import { TrendingUp, Award, Tag, Map, Sparkles } from 'lucide-react';

const fmt = (n) => (n || 0).toLocaleString();
const money = (cents) =>
  cents == null
    ? '—'
    : `$${(cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

function FunnelBar({ label, value, width, sub }) {
  return (
    <div className="mb-2" data-testid={`funnel-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex justify-between text-xs text-slate-600 mb-0.5">
        <span className="font-semibold">{label}</span>
        <span>{fmt(value)} {sub && <span className="text-slate-400">· {sub}</span>}</span>
      </div>
      <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-cyan-300 to-cyan-500 rounded-full"
          style={{ width: `${Math.max(2, width)}%` }}
        />
      </div>
    </div>
  );
}

const OBJECTIVE_KPIS = {
  awareness:  ['clicks', 'unique_visitors', 'conversions', 'revenue', 'roas'],
  conversion: ['conversions', 'revenue', 'roas', 'conversion_rate', 'clicks'],
  hybrid:     ['clicks', 'conversions', 'revenue', 'roas', 'conversion_rate'],
};

export default function CreatorAttributionPanel({ attribution, objective }) {
  if (!attribution) return null;
  const funnel = attribution.funnel || {};
  const clicks = funnel.clicks || 0;
  const unique = funnel.unique_visitors || 0;
  const conv = funnel.conversions || 0;
  const revenue = funnel.revenue_cents || 0;
  const cost = attribution.campaign_cost_cents || 0;
  const roas = attribution.roas;
  const cr = funnel.conversion_rate_pct;
  const max = Math.max(clicks, unique, conv, 1);

  const obj = (objective || 'hybrid').toLowerCase();
  const kpis = OBJECTIVE_KPIS[obj] || OBJECTIVE_KPIS.hybrid;

  return (
    <section className="mt-8" data-testid="creator-attribution-panel">
      <div className="flex items-center gap-3 mb-3">
        <Sparkles className="w-5 h-5 text-cyan-500" />
        <div>
          <h2 className="text-lg font-bold text-slate-900">Creator Attribution</h2>
          <p className="text-xs text-slate-500">
            {obj === 'awareness'
              ? 'Awareness → Engagement → Clicks → Reach'
              : obj === 'conversion'
                ? 'Clicks → Conversions → Revenue → ROAS'
                : 'Awareness → Engagement → Clicks → Leads → Sales → Revenue'}
          </p>
        </div>
      </div>

      {/* KPI strip — objective-aware */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
        {kpis.map((k) => {
          const tile = {
            clicks: { label: 'Clicks', value: fmt(clicks), tid: 'attr-clicks' },
            unique_visitors: { label: 'Unique Visitors', value: fmt(unique), tid: 'attr-unique' },
            conversions: { label: 'Conversions', value: fmt(conv), tid: 'attr-conv' },
            revenue: { label: 'Revenue', value: money(revenue), tid: 'attr-revenue' },
            roas: { label: 'ROAS', value: roas != null ? `${roas.toFixed(2)}×` : '—', tid: 'attr-roas' },
            conversion_rate: { label: 'Conv. Rate', value: cr != null ? `${cr.toFixed(2)}%` : '—', tid: 'attr-cr' },
          }[k];
          if (!tile) return null;
          return (
            <div key={k} className="bg-white border border-slate-200 rounded-xl p-3" data-testid={tile.tid}>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{tile.label}</div>
              <div className="text-xl font-bold text-slate-900 mt-1">{tile.value}</div>
            </div>
          );
        })}
      </div>

      {/* Funnel */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4">
        <div className="text-sm font-semibold text-slate-700 mb-2">Attribution Funnel</div>
        <FunnelBar label="Clicks" value={clicks} width={100} />
        <FunnelBar label="Unique Visitors" value={unique} width={(unique / max) * 100} />
        <FunnelBar label="Conversions" value={conv} width={(conv / max) * 100} />
        <FunnelBar label="Revenue" value={revenue} sub={money(revenue)}
          width={revenue ? Math.min(100, (conv / max) * 100) : 2} />
        {cost > 0 && (
          <div className="mt-3 text-xs text-slate-600">
            Cost: <b>{money(cost)}</b> · ROAS:{' '}
            <b>{roas != null ? `${roas.toFixed(2)}×` : '—'}</b>
          </div>
        )}
      </div>

      {/* Two-column: top creators + top promo codes */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-4">
        <div className="bg-white border border-slate-200 rounded-xl p-4"
          data-testid="top-creators-card">
          <div className="flex items-center gap-2 mb-2">
            <Award className="w-4 h-4 text-cyan-500" />
            <div className="text-sm font-semibold">Top Revenue-Generating Creators</div>
          </div>
          {(attribution.top_creators || []).length === 0 && (
            <p className="text-xs text-slate-500">No creator conversions yet.</p>
          )}
          <ul className="space-y-2">
            {(attribution.top_creators || []).map((c, i) => (
              <li key={c.creator_id + i} className="flex items-center justify-between border-b border-slate-100 pb-2 last:border-0"
                data-testid={`top-creator-${i}`}>
                <div>
                  <div className="text-sm font-medium text-slate-900">{c.name}</div>
                  <div className="text-xs text-slate-500">{fmt(c.conversions)} conv · {fmt(c.orders)} orders</div>
                </div>
                <div className="text-sm font-bold text-cyan-700">{money(c.revenue_cents)}</div>
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-4"
          data-testid="top-promo-codes-card">
          <div className="flex items-center gap-2 mb-2">
            <Tag className="w-4 h-4 text-cyan-500" />
            <div className="text-sm font-semibold">Top Performing Promo Codes</div>
          </div>
          {(attribution.top_promo_codes || []).length === 0 && (
            <p className="text-xs text-slate-500">No promo code conversions yet.</p>
          )}
          <ul className="space-y-2">
            {(attribution.top_promo_codes || []).map((p, i) => (
              <li key={p.code} className="flex items-center justify-between border-b border-slate-100 pb-2 last:border-0"
                data-testid={`top-promo-${i}`}>
                <div>
                  <div className="text-sm font-mono font-bold text-slate-900">{p.code}</div>
                  <div className="text-xs text-slate-500">{fmt(p.uses)} uses · {fmt(p.orders)} orders</div>
                </div>
                <div className="text-sm font-bold text-cyan-700">{money(p.revenue_cents)}</div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Revenue breakdowns */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="attribution-breakdowns">
        <BreakdownCard
          title="Revenue by Market"
          icon={Map}
          rows={(attribution.revenue_by_market || []).map((r) => ({
            label: r.market || 'Unknown',
            value: r.revenue_cents,
          }))}
        />
        <BreakdownCard
          title="Revenue by Campaign Type"
          icon={TrendingUp}
          rows={(attribution.revenue_by_campaign_type || []).map((r) => ({
            label: (r.campaign_type || 'unknown').replace(/_/g, ' '),
            value: r.revenue_cents,
          }))}
        />
        <BreakdownCard
          title="Revenue by Objective"
          icon={Sparkles}
          rows={(attribution.revenue_by_objective || []).map((r) => ({
            label: (r.objective || 'unspecified').charAt(0).toUpperCase() + (r.objective || '').slice(1),
            value: r.revenue_cents,
          }))}
        />
      </div>
    </section>
  );
}

function BreakdownCard({ title, icon: Icon, rows }) {
  const total = rows.reduce((s, r) => s + (r.value || 0), 0) || 1;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4"
      data-testid={`breakdown-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center gap-2 mb-2">
        {Icon && <Icon className="w-4 h-4 text-cyan-500" />}
        <div className="text-sm font-semibold">{title}</div>
      </div>
      {rows.length === 0 && <p className="text-xs text-slate-500">No data yet.</p>}
      <ul className="space-y-2">
        {rows.slice(0, 6).map((r, i) => (
          <li key={r.label + i}>
            <div className="flex justify-between text-xs">
              <span className="text-slate-700 truncate mr-2">{r.label}</span>
              <span className="text-slate-900 font-semibold">{money(r.value)}</span>
            </div>
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mt-0.5">
              <div className="h-full bg-cyan-400 rounded-full"
                style={{ width: `${Math.max(2, ((r.value || 0) / total) * 100)}%` }} />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
